import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { DispatchAssetDialog } from "@/components/order/DispatchAssetDialog";
import { Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { MovementDialog } from "@/components/logistics/MovementForm";
import { DispatchSheet } from "@/components/logistics/DispatchSheet";
import { fmtDate } from "@/lib/format";

const DISPATCHABLE = ["approved", "in_production", "partially_dispatched"];

export const docRef = (d) => (d.document_type === "delivery_challan" ? `DC ${d.dc_no}` : `Inv ${d.invoice_no}`);
export const itemsText = (d) => d.items.map((i) => `${i.qty} × ${i.description}`).join("; ");

export const DSP_COLS = [
  { key: "dispatch_no", label: "Dispatch", render: (d) => <span className="font-mono font-semibold text-sky-800">{d.dispatch_no}</span> },
  { key: "dispatch_date", label: "Date", render: (d) => fmtDate(d.dispatch_date) },
  { key: "doc", label: "Invoice / DC no.", mono: true, value: docRef, render: docRef },
  { key: "movement_no", label: "Movement", mono: true },
  { key: "order_no", label: "LM", mono: true },
  { key: "customer_name", label: "Customer", wrap: true },
  { key: "items", label: "Items", wrap: true, value: itemsText, render: itemsText },
  { key: "transporter", label: "Transporter", value: (d) => d.transporter?.name || d.transporter, render: (d) => d.transporter?.name || d.transporter || "—" },
  { key: "vehicle_no", label: "Vehicle", mono: true },
  { key: "lr_no", label: "LR / Docket", mono: true },
  { key: "destination", label: "Destination", wrap: true },
  { key: "expected_delivery_date", label: "Expected", render: (d) => fmtDate(d.expected_delivery_date) },
  { key: "delivered_date", label: "Delivered", render: (d) => fmtDate(d.delivered_date) },
  { key: "dispatch_status", label: "Dispatch", exp: (d) => d.dispatch_status || "dispatched", render: (d) => <StatusBadge status={d.dispatch_status || "dispatched"} /> },
  { key: "delivery_status", label: "Delivery", render: (d) => <StatusBadge status={d.delivery_status} testid={`delivery-status-${d.dispatch_no}`} /> },
  { key: "pod_status", label: "POD", render: (d) => <StatusBadge status={`pod_${d.pod_status || "pending"}`} testid={`pod-status-${d.dispatch_no}`} /> },
];

export function DispatchesTab({ order, reload }) {
  const { can } = useMe();
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(null);
  const [sp, setSp] = useSearchParams();
  const [assetFor, setAssetFor] = useState(sp.get("assets"));
  const { data: pend, reload: again } = useFetch(`/assets/from-dispatch?order_id=${order.id}`);
  const pending = useMemo(() => Object.fromEntries((pend || []).map((p) => [p.id, p.rule])), [pend]);
  const canAssets = can("assets.edit");
  const closeAssets = () => { setAssetFor(null); if (sp.get("assets")) { sp.delete("assets"); setSp(sp, { replace: true }); } };
  const columns = useMemo(() => [...DSP_COLS, { key: "assets", label: "Assets", exp: false, render: (d) => (pending[d.id] && canAssets
    ? <Button size="sm" variant={pending[d.id] === "Required" ? "default" : "outline"} className="h-7" onClick={(e) => { e.stopPropagation(); setAssetFor(d.id); }} data-testid={`dispatch-create-assets-${d.dispatch_no}`}>{pending[d.id] === "Required" ? "Create assets (required)" : "Create assets"}</Button>
    : <span className="text-xs text-slate-500">{{ done: "Added", partial: "Partly added", skipped: "No assets" }[d.asset_prompt] || "—"}</span>) }], [pending, canAssets]);
  const allowed = DISPATCHABLE.includes(order.status) && can("write") && !order.is_projection;
  return (
    <div data-testid="dispatches-tab">
      <div className="mb-3 flex items-center gap-3">
        {allowed && <Button size="sm" onClick={() => setOpen(true)} data-testid="new-dispatch-btn"><Truck className="h-4 w-4 mr-1" />New dispatch / movement</Button>}
        {!DISPATCHABLE.includes(order.status) && <span className="text-xs text-slate-500">Dispatch opens once the LM is approved.</span>}
        {order.is_projection && <span className="text-xs text-violet-700">Projection — record the PO before dispatch.</span>}
      </div>
      <DataTable testid="lm-dispatches-table" columns={columns} rows={order.dispatches} onRowClick={setFocus} empty="No dispatches yet" />
      {assetFor && <DispatchAssetDialog dispatchId={assetFor} onClose={closeAssets} onDone={() => { again(); reload(); }} />}
      {open && <MovementDialog open={open} onOpenChange={setOpen} presetOrder={order} onSaved={reload} />}
      <DispatchSheet dispatch={focus} onClose={() => setFocus(null)} onDone={() => { setFocus(null); reload(); }} />
    </div>
  );
}
